import type { AnyRouter, ParsedLocation } from "@tanstack/react-router";
import { THEME_COLOR } from "@/lib/useThemeColor";

/**
 * Screen transitions (ADR-0052) — the browser **View Transitions API** driven through
 * TanStack Router. This module owns the pure primitives the baseline needs (navigation
 * direction + feature detection), the pure route predicates that pick each boundary's
 * character, and the DOM wiring that (a) exposes the direction on `<html>` **before** the
 * transition starts so CSS can branch on it and (b) installs the fade-through overlay veil.
 *
 * Continuity, not choreography (CONTEXT.md "Screen transition"): most boundaries are a
 * symmetric root cross-fade; the direction signal only picks the forward/back variant of the
 * warm Landing → Stage dissolve (#346); no boundary morphs individual elements. The ONE
 * exception to the root cross-fade is the full-bleed ↔ chrome crossing, which uses a real DOM
 * overlay veil (a uniform fade THROUGH the destination colour) precisely because its
 * mismatched layouts break the root snapshot on WebKit — see `isFullBleedChromeCrossing`.
 */

export type NavDirection = "forward" | "back";

/** dataset key on `<html>` — camelCase `navDirection` renders as `data-nav-direction`. */
export const NAV_DIRECTION_ATTR = "navDirection" as const;

/**
 * The symmetric default direction, in force before any navigation happens (first paint /
 * direct entry). The document shell (routes/__root.tsx) RENDERS it on `<html>` so the
 * server HTML already carries it: `installScreenTransitions` runs inside the isomorphic
 * router factory, i.e. BEFORE `hydrateRoot`, so a purely imperative default would add an
 * attribute the client tree does not have and React would report a hydration mismatch on
 * `<html>` (ADR-0053 made the app server-rendered). Shell + imperative write must therefore
 * agree on this one value.
 */
export const NAV_DIRECTION_DEFAULT: NavDirection = "forward";

/**
 * Pure direction primitive: derive the funnel navigation direction from the router's
 * monotonic history index delta (TanStack's `__TSR_index`).
 *
 * - going to a **lower** index than we came from → `"back"` (browser/gesture back,
 *   `history.go(-n)`);
 * - anything else — a higher index, an equal index (a `replace`/redirect has no
 *   backward motion), or a first entry with no previous index → `"forward"`.
 *
 * The baseline cross-fade is symmetric, so the exact value only matters to the future
 * accents; `"forward"` is the safe, calm default whenever direction is indeterminate.
 */
export function navDirection(
  fromIndex: number | undefined,
  toIndex: number | undefined,
): NavDirection {
  if (typeof fromIndex !== "number" || typeof toIndex !== "number") return "forward";
  return toIndex < fromIndex ? "back" : "forward";
}

/** Read TanStack's monotonic history index off a parsed location, if present. */
export function historyIndexOf(location: ParsedLocation | undefined): number | undefined {
  // `state` is typed as always-present, but it originates from the browser history
  // (external): stay defensive so a stateless entry yields undefined, not a throw.
  return location?.state?.__TSR_index;
}

/**
 * Feature-detect the browser View Transitions API. This is the single fallback
 * contract: where it is `false` the whole effect degrades to an instant cut. (The
 * router itself also guards `document.startViewTransition`, so navigation never throws
 * or hangs on browsers without the API — pre-iOS-18 / pre-Chrome-111 — or in jsdom.)
 */
export function supportsViewTransitions(
  doc: Document | undefined = typeof document !== "undefined" ? document : undefined,
): boolean {
  return typeof doc?.startViewTransition === "function";
}

/** Routes backed by the ONE persistent Stage canvas (`/upload` + `/preview`). */
export function isStageHostRoute(pathname: string): boolean {
  return pathname === "/upload" || pathname.startsWith("/preview");
}

/**
 * Whether the router should SKIP the view transition for a navigation (ADR-0052).
 * The `/upload ↔ /preview` crossing is left transition-free: the persistent Stage
 * canvas + the Reveal already own that continuity, and a root snapshot would only
 * freeze a live frame and contend with the Reveal's frame budget. Skipping is decided
 * purely from the pathnames, so no host-route file has to know about transitions.
 */
export function shouldSkipViewTransition(
  fromPathname: string | undefined,
  toPathname: string,
): boolean {
  return (
    fromPathname !== undefined && isStageHostRoute(fromPathname) && isStageHostRoute(toPathname)
  );
}

/**
 * The View Transition TYPE that brands the warm Landing → Stage dissolve (#346). Applied
 * on the `/` ↔ `/upload` crossing so CSS can select it (`:active-view-transition-type()`)
 * WITHOUT disturbing the symmetric baseline cross-fade everywhere else. The literal MUST
 * match the `:active-view-transition-type(warm-dissolve)` selectors in `index.css`.
 */
export const WARM_DISSOLVE_TYPE = "warm-dissolve" as const;

/**
 * The Landing ↔ Stage-studio crossing (`/` ↔ `/upload`), in either direction — the one
 * boundary that gets the warm "entering the studio" dissolve (ADR-0052 accent #2). Both
 * directions match: forward blooms the studio up, back plays it in reverse (the CSS
 * branches forward/back off the `data-nav-direction` primitive).
 */
export function isLandingStudioCrossing(
  fromPathname: string | undefined,
  toPathname: string,
): boolean {
  if (fromPathname === undefined) return false;
  return (
    (fromPathname === "/" && toPathname === "/upload") ||
    (fromPathname === "/upload" && toPathname === "/")
  );
}

/**
 * Routes that render EDGE-TO-EDGE — no centered `max-w` column: the full-bleed landing +
 * comparison marketing pages, the immersive full-screen Stage routes (`/upload`,
 * `/preview`, `/stage-lab`), and the bare screenshot harness (`/shot`). Everything else
 * renders in the CENTERED, opaque chrome shell (Layout.tsx). Kept in step with Layout's
 * own `FULLSCREEN_PREFIXES` / `BARE_PREFIXES` — a cross-fade between a full-bleed page and
 * a centered column is the mismatch `isLandingChromeCrossing` singles out.
 */
function rendersFullBleed(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/comparison" ||
    isStageHostRoute(pathname) || // /upload + /preview
    pathname.startsWith("/stage-lab") ||
    pathname.startsWith("/shot")
  );
}

/**
 * The View Transition TYPE that marks a UNIFORM FADE-THROUGH-COLOUR crossing — a full-bleed
 * route (the landing / comparison / the full-screen Stage) crossing to or from a CENTERED
 * chrome-shell route (checkout, /me, /order, the legal pages …). The literal is a signal
 * for `installScreenTransitions`, which intercepts it and drives its OWN overlay veil
 * instead of the native root cross-fade — see `isFullBleedChromeCrossing`.
 */
export const FADE_THROUGH_TYPE = "fade-through" as const;

/**
 * A crossing between a FULL-BLEED route and a CENTERED chrome-shell route, in either
 * direction (XOR): exactly one side renders edge-to-edge and the other in the narrow
 * `max-w` column. The important members are `/preview ↔ /checkout` (leaving the immersive
 * viewer for the checkout summary) and the landing `/` ↔ any chrome page. A crossing where
 * BOTH sides are full-bleed (`/` ↔ /upload, /upload ↔ /preview) or BOTH are chrome
 * (checkout ↔ /regulamin) is NOT this — those keep their own handling (warm dissolve /
 * skip) or the calm baseline cross-fade.
 *
 * Why these get a bespoke overlay veil rather than the native root cross-fade: the two
 * layouts mismatch hard (a full-bleed page vs a narrow centered column). A root snapshot
 * of that mismatch renders BADLY — on Chromium both pages overlap mid-transition (one
 * "collapsing into a column"); on WebKit it is worse, mis-scaling the full-bleed root
 * snapshot into a small centered box (the shrink is inherent to WebKit's root-transition
 * capture here, independent of the fade timing). A sequenced "fade through paper" built on
 * the same snapshot fixed Chromium but WebKit still shrank it (operator report 2026-07-16,
 * verified in WebKit). Since ANY effect that rides the root snapshot inherits the WebKit
 * bug, the only cross-engine-robust fix is to take NO snapshot at all and instead animate a
 * real DOM overlay (see `installScreenTransitions` → the veil): cover the old page, swap the
 * route while covered, reveal the new page. Identical in every engine, no element lags, no
 * position jump. This replaces the previous instant-cut compromise for the landing ↔ chrome
 * crossing with a proper symmetric fade.
 */
export function isFullBleedChromeCrossing(
  fromPathname: string | undefined,
  toPathname: string,
): boolean {
  if (fromPathname === undefined) return false;
  return rendersFullBleed(fromPathname) !== rendersFullBleed(toPathname);
}

/**
 * The destination background colour the veil fades THROUGH for a fade-through crossing — the
 * colour the arriving page is painted on, so the reveal starts from the right surface and
 * there is no colour "pop" at the swap: the dark-warm Stage (`THEME_COLOR.stage`) when the
 * destination is a full-bleed Stage/landing route, paper (`THEME_COLOR.paper`) for a chrome
 * page. (A single neutral was considered; fading to the destination colour is what removes
 * the flash — the incoming page's own background continues the veil seamlessly.)
 */
export function veilColorFor(toPathname: string): string {
  return rendersFullBleed(toPathname) ? THEME_COLOR.stage : THEME_COLOR.paper;
}

/**
 * The View Transition `types` for a navigation (ADR-0052), decided purely from the
 * pathnames:
 * - `false` → SKIP the transition entirely: only the `/upload ↔ /preview` persistent-canvas
 *   crossing (the Reveal + the shared canvas own that continuity);
 * - `[FADE_THROUGH_TYPE]` → a full-bleed ↔ chrome crossing whose mismatched layouts a root
 *   snapshot renders badly; `installScreenTransitions` intercepts this type and runs its own
 *   overlay veil (never a root snapshot — see `isFullBleedChromeCrossing`);
 * - `[WARM_DISSOLVE_TYPE]` → brand the warm `/` ↔ `/upload` studio dissolve;
 * - `[]` → the calm symmetric root cross-fade baseline on every other boundary
 *   (chrome ↔ chrome, and same-layout full-bleed ↔ full-bleed like `/` ↔ /comparison).
 *
 * The fade-through type is deliberately NOT `false`: returning `false` makes the router skip
 * `document.startViewTransition` and cut instantly, but the veil is driven from INSIDE the
 * patched `startViewTransition`, so the crossing must reach it. `[FADE_THROUGH_TYPE]` gets
 * the router to call `startViewTransition` (with or without type support) where the patch
 * takes over — the native root snapshot is never actually captured for these.
 *
 * The router consults `types` only where the browser supports view-transition types
 * (Chrome 125+ / Safari 18.2+); on the narrow older VT window the type array is ignored but
 * `startViewTransition` is still called, so the veil interception (which keys off the
 * captured crossing, not the type) still runs — graceful, not broken.
 */
export function viewTransitionTypesFor(
  fromPathname: string | undefined,
  toPathname: string,
): string[] | false {
  if (shouldSkipViewTransition(fromPathname, toPathname)) return false;
  if (isFullBleedChromeCrossing(fromPathname, toPathname)) return [FADE_THROUGH_TYPE];
  if (isLandingStudioCrossing(fromPathname, toPathname)) return [WARM_DISSOLVE_TYPE];
  return [];
}

/**
 * The `id` of the full-viewport overlay veil the fade-through crossings animate. Mounted
 * once by the root route (routes/__root.tsx) as a fixed, pointer-transparent div above
 * every other layer; the driver below finds it by id and animates only its opacity. Kept in
 * sync with the `id` on that element.
 */
export const VEIL_ID = "screen-transition-veil";

/** Duration of ONE leg (cover, or reveal) of the fade-through veil, ms. ~200ms ease-out
 * each way ≈ a 400ms round trip — a real "fade out → fade in", not a cut, within the
 * ADR-0052 calm-continuity budget. */
const VEIL_FADE_MS = 200;
/** Reduced-motion: a very short opacity blip instead of the full fade (mirrors how the
 * baseline cross-fade / Reveal degrade in index.css) — the page still covers before the
 * swap so no mismatched frame is ever shown, it just settles almost instantly. */
const VEIL_FADE_MS_REDUCED = 60;

type WindowLike = Pick<Window, "matchMedia" | "requestAnimationFrame">;

function fadeDurationMs(win: WindowLike | undefined): number {
  const reduced = win?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  return reduced ? VEIL_FADE_MS_REDUCED : VEIL_FADE_MS;
}

/** Resolve AFTER the next paint (two rAFs) so the freshly-swapped route has actually
 * rendered before the veil starts revealing it — this is what prevents a bare/half-painted
 * new page flashing through mid-reveal. Falls back to a resolved promise without rAF. */
function afterNextPaint(win: WindowLike | undefined): Promise<void> {
  if (!win?.requestAnimationFrame) return Promise.resolve();
  return new Promise((resolve) => {
    win.requestAnimationFrame(() => win.requestAnimationFrame(() => resolve()));
  });
}

/** Animate the veil's opacity from → to (via the Web Animations API), optionally repainting
 * it `color` first. Cancels any in-flight animation so overlapping navigations don't stack,
 * and pins the final opacity as an inline style so the veil holds its end state after the
 * (filled) animation is cleared. */
async function fadeVeil(
  veil: HTMLElement,
  from: number,
  to: number,
  color: string | undefined,
  win: WindowLike | undefined,
): Promise<void> {
  if (color) veil.style.backgroundColor = color;
  veil.style.opacity = String(from);
  // Cancel any in-flight fade so overlapping navigations don't stack (both guarded — the
  // Web Animations API is absent under jsdom / very old engines).
  if (typeof veil.getAnimations === "function") {
    for (const anim of veil.getAnimations()) anim.cancel();
  }
  // element.animate is absent under jsdom / very old engines — fall back to an instant set.
  if (typeof veil.animate !== "function") {
    veil.style.opacity = String(to);
    return;
  }
  const animation = veil.animate([{ opacity: from }, { opacity: to }], {
    duration: fadeDurationMs(win),
    easing: "ease-out",
    fill: "forwards",
  });
  await animation.finished.catch(() => {});
  veil.style.opacity = String(to);
  animation.cancel();
}

/**
 * Drive ONE symmetric fade-through crossing on the overlay veil: cover the OLD page (fade
 * the veil IN to `color`), run `update()` (the router's DOM/route swap) while fully covered,
 * wait for the new page to paint, then reveal it (fade the veil OUT). This is engine-
 * independent — it NEVER calls `document.startViewTransition`, so the mismatched full-bleed ↔
 * chrome pages are never snapshotted (the WebKit root-snapshot bug, see
 * `isFullBleedChromeCrossing`). The swap is always run, even if the veil element is missing
 * (SSR/tests) or a fade throws, so navigation can never get stuck behind the veil.
 */
async function runFadeThrough(
  doc: Document,
  color: string,
  update: () => unknown,
  win: WindowLike | undefined,
): Promise<void> {
  const veil = doc.getElementById(VEIL_ID);
  if (!veil) {
    await update();
    return;
  }
  try {
    await fadeVeil(veil, 0, 1, color, win); // cover the old page
    await update(); // swap the route while covered
    await afterNextPaint(win); // let the new page paint before we uncover
  } finally {
    await fadeVeil(veil, 1, 0, undefined, win); // reveal the new page
  }
}

/** A minimal object shaped like the DOM `ViewTransition` the router expects back from
 * `document.startViewTransition`. The router fires the transition and does not await the
 * return, so resolving all three promises when the veil sequence completes is enough. */
function veilViewTransition(finished: Promise<void>): ViewTransition {
  return {
    finished,
    ready: finished,
    updateCallbackDone: finished,
    skipTransition() {},
    types: new Set<string>(),
  } as unknown as ViewTransition;
}

/**
 * Expose the navigation direction on `<html data-nav-direction>` and install the fade-through
 * veil interception (ADR-0052). Subscribes to `onBeforeNavigate`, which the router emits
 * synchronously *before* it calls `document.startViewTransition` — so both the direction
 * attribute (for the warm-dissolve CSS) and the "is this a fade-through crossing?" decision
 * are committed before the transition starts. Returns an unsubscribe/uninstall fn.
 *
 * The fade-through is delivered by monkey-patching `document.startViewTransition`: for a
 * full-bleed ↔ chrome crossing the patch runs the overlay veil (`runFadeThrough`) INSTEAD of
 * the native root cross-fade — deliberately taking no root snapshot, because that snapshot is
 * exactly what WebKit mis-scales on these mismatched layouts (see `isFullBleedChromeCrossing`).
 * Every other crossing is forwarded untouched to the original API, so the baseline cross-fade
 * and the warm dissolve are unchanged. The crossing is captured from the pathnames (not the
 * VT `types`), so the veil runs even on the older VT-without-types engines.
 *
 * Everything is installed only where the View Transitions API exists — without it the router
 * never calls `startViewTransition` (instant cut), nothing consumes the direction attribute,
 * and there is nothing to patch.
 */
export function installScreenTransitions(
  router: AnyRouter,
  doc: Document | undefined = typeof document !== "undefined" ? document : undefined,
  win: WindowLike | undefined = typeof window !== "undefined" ? window : undefined,
): () => void {
  const root = doc && supportsViewTransitions(doc) ? doc.documentElement : undefined;
  if (!doc || !root) {
    // No View Transitions API (SSR / jsdom / old engine): still subscribe so the pure
    // direction primitive stays exercised, but write nothing and patch nothing.
    return router.subscribe("onBeforeNavigate", () => {});
  }

  // Symmetric default before any navigation (first paint / direct entry). Under SSR this
  // re-writes the value the shell already rendered (see NAV_DIRECTION_DEFAULT); it still
  // matters for a client-only mount (tests, non-prerendered entries).
  root.dataset[NAV_DIRECTION_ATTR] = NAV_DIRECTION_DEFAULT;

  // The veil colour captured for the NEXT navigation, or null when it is not a fade-through
  // crossing. Set in onBeforeNavigate (before the transition), consumed once by the patched
  // startViewTransition. A single-slot handoff is safe: the router runs one transition at a
  // time and consumes it on the very next startViewTransition call.
  let pendingVeilColor: string | null = null;

  // Keep the RAW reference (no .bind) so uninstall can restore the exact original and repeated
  // install/uninstall cycles never stack. The forward call re-supplies `this` via .call.
  const originalStart = doc.startViewTransition;
  const patchedStart = ((callbackOrOptions: unknown) => {
    const color = pendingVeilColor;
    pendingVeilColor = null;
    if (color !== null) {
      const update =
        typeof callbackOrOptions === "function"
          ? (callbackOrOptions as () => unknown)
          : (callbackOrOptions as { update: () => unknown }).update;
      return veilViewTransition(runFadeThrough(doc, color, update, win));
    }
    return originalStart.call(doc, callbackOrOptions as never);
  }) as typeof doc.startViewTransition;
  doc.startViewTransition = patchedStart;

  const unsubscribe = router.subscribe("onBeforeNavigate", ({ fromLocation, toLocation }) => {
    root.dataset[NAV_DIRECTION_ATTR] = navDirection(
      historyIndexOf(fromLocation),
      historyIndexOf(toLocation),
    );
    pendingVeilColor = isFullBleedChromeCrossing(fromLocation?.pathname, toLocation.pathname)
      ? veilColorFor(toLocation.pathname)
      : null;
  });

  return () => {
    unsubscribe();
    // Restore the original only if nothing else re-patched over us in the meantime.
    if (doc.startViewTransition === patchedStart) doc.startViewTransition = originalStart;
  };
}
